import { createAsyncThunk } from '@reduxjs/toolkit';
import getChartDataUseCase from 'modules/inbound/entriesST12/application/getChartDataUseCase';
import {
  ChartPoint,
  ChartPointType,
  ExternalChartPointType
} from 'modules/inbound/entriesST12/domain/chartData';

const MADRID_TIME_ZONE = 'Europe/Madrid';

const formatUtcDateTime = (date: Date) =>
  date.toISOString().slice(0, 16).replace('T', ' ');

const getMadridDateParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MADRID_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(date);

  return parts.reduce<Record<string, string>>((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
};

const madridWallClockToUtc = (dateTime: string) => {
  const wallClockAsUtc = new Date(`${dateTime.replace(' ', 'T')}:00Z`);
  const madridParts = new Intl.DateTimeFormat('en-CA', {
    timeZone: MADRID_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(wallClockAsUtc);

  const values = madridParts.reduce<Record<string, string>>((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  const madridWallClockAsUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );
  const requestedWallClock = wallClockAsUtc.getTime();

  return new Date(
    requestedWallClock - (madridWallClockAsUtc - requestedWallClock)
  );
};

const addCalendarDay = (date: Date) => {
  const next = new Date(`${date.toISOString().slice(0, 10)}T00:00:00Z`);
  next.setUTCDate(next.getUTCDate() + 1);
  return next.toISOString().slice(0, 10);
};

export const buildTomorrowRange = (date: Date = new Date()) => {
  const next24Hours = new Date(date.getTime() + 24 * 60 * 60 * 1000);

  return {
    start_date_hour_range: formatUtcDateTime(date),
    end_date_hour_range: formatUtcDateTime(next24Hours)
  };
};

export const buildPreviousDayRange = (date: Date = new Date()) => {
  const madridDateParts = getMadridDateParts(date);
  const currentMadridDate = new Date(
    `${madridDateParts.year}-${madridDateParts.month}-${madridDateParts.day}T00:00:00Z`
  );
  currentMadridDate.setUTCDate(currentMadridDate.getUTCDate() - 1);

  const startMadrid = `${currentMadridDate.toISOString().slice(0, 10)} 05:45`;
  const endMadrid = `${addCalendarDay(currentMadridDate)} 05:45`;

  return {
    start_date_hour_range: formatUtcDateTime(madridWallClockToUtc(startMadrid)),
    end_date_hour_range: formatUtcDateTime(madridWallClockToUtc(endMadrid))
  };
};
const createGetChartDataThunk = (
  typePrefix: string,
  unloadingType: 'packages' | 'trucks',
  booked: number,
  filterBy: ChartPoint[] = [ChartPointType.T12E],
  workshopType = 'T12'
) =>
  createAsyncThunk(typePrefix, async () => {
    //const todayDate = new Date();
    const customDate = new Date();
    //customDate.setFullYear(2026, 6, 28);

    const { start_date_hour_range, end_date_hour_range } =
      booked === 1
        ? buildTomorrowRange(customDate)
        : buildPreviousDayRange(customDate);

    const queryParams = {
      start_date_hour_range,
      end_date_hour_range,
      unloading_type: unloadingType,
      workshop_type: workshopType,
      interval: '1h',
      booked: booked,
      start_minute_offset: 45,
      workshop_query: 'true'
    };

    const response = await getChartDataUseCase.execute({
      queryParams,
      filterBy
    });
    return response;
  });

export const getPackagesChartData = createGetChartDataThunk(
  'entriesST12/getPackagesChartData',
  'packages',
  1
);

export const getTrunkChartData = createGetChartDataThunk(
  'entriesST12/getTrunkChartData',
  'trucks',
  1
);

export const getUnloadedTrunkChartData = createGetChartDataThunk(
  'entriesST12/getUnloadedTrunkChartData',
  'trucks',
  0
);

export const getT12EDetailUnloadedTrunkChartData = createGetChartDataThunk(
  'entriesST12/getT12EDetailUnloadedTrunkChartData',
  'trucks',
  0,
  [ExternalChartPointType.T12E_EXT, ExternalChartPointType.T12E_EX1],
  'T12E'
);
